import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { UtilsService } from '@services/common/utils.service';
import { OrganizationAction } from '@state/organization.state';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { ConfirmationService } from 'primeng/api';
import { combineLatest, Subject } from 'rxjs';

@Component({
    selector: 'app-edit-period',
    templateUrl: './edit-period.component.html',
    styleUrls: ['./edit-period.component.scss'],
})
export class EditPeriodComponent {
    private _period: number;

    // The @Input() decorator marks this setter as an input property,
    // meaning it can receive values from a parent component.
    @Input()
    set period(value: any) {
        // If the value is an object and has a 'period' property, assign that to _period.
        // Otherwise, just assign the value itself.
        this._period = value && typeof value === 'object' ? value?.period : value;
    }

    // Getter to access the normalized "period" value
    get period(): number {
        return this._period;
    }
    @Input() public availableYears: number[];
    @Input() public organizationId: string;
    @Input() public activeQuestionnaire: QuestionnaireDto;
    @Input() public questionnaires: QuestionnaireDto[];
    @Output() closeEditPeriod = new EventEmitter<{ translateKey: string; status: boolean } | undefined>();
    public yearInDropdown: number[];
    public periodForm: FormGroup;

    constructor(
        private fb: FormBuilder,
        private store: Store,
        private confirmationService: ConfirmationService,
        private translateService: TranslateService,
        private utilsService: UtilsService,
    ) {
        this.periodForm = this.fb.group({
            year: new FormControl('', Validators.required),
        });
    }

    ngOnInit(): void {
        this.yearInDropdown = [...this.availableYears];
        if (this.period !== null) {
            this.yearInDropdown.push(this.period);
            this.yearInDropdown.sort((a, b) => a - b);
            this.periodForm.get('year')?.setValue(this.period);
        }
    }

    save(): void {
        if (this.period === null) {
            this.createQuestionnaires(this.periodForm.get('year')!.value);
        } else {
            this.updateQuestionnairesPeriod();
        }
    }

    createQuestionnaires(year: number) {
        if (this.periodForm.valid) {
            let callCompletion: Subject<QuestionnaireDto[] | undefined> = new Subject();
            this.store.dispatch(
                new OrganizationAction.AddQuestionnairesFromPeriodAction(this.organizationId, year, callCompletion),
            );
            callCompletion.subscribe({
                next: () => {
                    this.closeEditPeriod.emit({
                        translateKey: 'components.settingsForm.periodSection.toasts.creationSuccess',
                        status: true,
                    });
                },
                error: () => {
                    this.closeEditPeriod.emit({
                        translateKey: 'components.settingsForm.periodSection.toasts.creationFail',
                        status: false,
                    });
                },
            });
        }
    }

    onLabelClick(id: string) {
        const primeInput = document.getElementById(id);
        if (primeInput) {
            const input = this.getInputElement(primeInput);
            input?.focus();
        }
    }
    getInputElement(primeInput: HTMLElement): HTMLElement | null {
        return (
            primeInput.querySelector('p-dropdown') ||
            primeInput.querySelector('p-radiobutton') ||
            primeInput.querySelector('input')
        );
    }

    public updateQuestionnairesPeriod() {
        if (this.period) {
            const callCompletion = new Subject<QuestionnaireDto[] | undefined>();
            this.store.dispatch(
                new OrganizationAction.EditQuestionnairesPeriodAction(
                    this.organizationId,
                    this.period,
                    this.periodForm.get('year')!.value,
                    this.activeQuestionnaire,
                    callCompletion,
                ),
            );
            callCompletion.subscribe({
                next: () => {
                    this.closeEditPeriod.emit({
                        translateKey: 'components.settingsForm.periodSection.toasts.updateSuccess',
                        status: true,
                    });
                },
                error: () => {
                    this.closeEditPeriod.emit({
                        translateKey: 'components.settingsForm.periodSection.toasts.updateFail',
                        status: false,
                    });
                },
            });
        }
    }

    onDelete(): void {
        combineLatest([
            this.translateService.get('common.confirm_message'),
            this.translateService.get('common.confirm_title'),
            this.translateService.get('common.confirm_button'),
            this.translateService.get('common.cancel_button'),
        ]).subscribe((translations) => {
            this.confirmationService.confirm({
                message: this.utilsService.handleTranslation(translations[0]),
                header: this.utilsService.handleTranslation(translations[1]),
                acceptLabel: this.utilsService.handleTranslation(translations[2]),
                rejectLabel: this.utilsService.handleTranslation(translations[3]),
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    if (this.period) {
                        const callCompletion = new Subject<QuestionnaireDto[] | undefined>();
                        this.store.dispatch(
                            new OrganizationAction.DeleteQuestionnairesFromPeriodAction(
                                this.organizationId,
                                this.period,
                                this.activeQuestionnaire,
                                callCompletion,
                            ),
                        );
                        callCompletion.subscribe({
                            next: () => {
                                this.closeEditPeriod.emit({
                                    translateKey: 'components.settingsForm.periodSection.toasts.deteteSuccess',
                                    status: true,
                                });
                            },
                            error: () => {
                                this.closeEditPeriod.emit({
                                    translateKey: 'components.settingsForm.periodSection.toasts.deleteFail',
                                    status: false,
                                });
                            },
                        });
                    }
                },
                reject: (type) => {},
            });
        });
    }

    close(): void {
        this.closeEditPeriod.emit();
    }
}
